import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputAdornment,
  LinearProgress,
  Menu,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { SectionPaper } from "@/components/Paper";
import { toast } from "react-toastify";
import {
  type MapPlotRequestDetailFile,
  type MapPlotRequestDetailFileGroup,
  type SavedMapPlotRequestDetail,
} from "@/features/map/types";
import {
  DEFAULT_MAP_PLOT_REQUEST_DETAIL_FILE_SORT_BY,
  DEFAULT_MAP_PLOT_REQUEST_DETAIL_FILE_SORT_ORDER,
  MAP_PLOT_REQUEST_DETAIL_FILE_GROUPS,
  MAP_PLOT_REQUEST_DETAIL_FILE_SORT_BY_VALUES,
  MAP_PLOT_REQUEST_DETAIL_FILE_SORT_ORDER_VALUES,
  type MapPlotRequestDetailFileSortBy,
  type MapPlotRequestDetailFileSortOrder,
} from "@/features/mapPlotRequest/api/mapPlotRequestDetailFiles";
import { DeletePlotDetailFileConfirmModal } from "@/features/mapPlotRequest/components/DeletePlotDetailFileConfirmModal";
import { useMapPlotRequestDetailFiles } from "@/features/mapPlotRequest/hooks/useMapPlotRequestDetailFiles";
import { formatMapPlotRequestDateTime } from "@/features/mapPlotRequest/utils/dateTime";
import {
  MAP_PLOT_REQUEST_DETAIL_ALLOWED_EXTENSIONS,
  extractMapPlotRequestDetailFileErrorMessage,
  formatMapPlotRequestDetailFileSize,
  isPreviewableMapPlotRequestDetailFileContentType,
  isSupportedMapPlotRequestDetailFile,
} from "@/features/mapPlotRequest/utils/mapPlotRequestDetailFiles";
import { formatNumberWithComma } from "@/utils/formatNumber";

interface MapPlotRequestDetailFilesViewProps {
  detail: SavedMapPlotRequestDetail;
}

interface MapPlotRequestDetailFileSection {
  key: string;
  label: string;
  group: MapPlotRequestDetailFileGroup | null;
  files: MapPlotRequestDetailFile[];
}

interface DisplayNameFormData {
  displayName: string;
}

const MAX_DISPLAY_NAME_LENGTH = 255;
const SORT_BY_LABELS: Record<MapPlotRequestDetailFileSortBy, string> = {
  uploaded_at: "アップロード日時",
  name: "表示名",
  file_type: "ファイル形式",
};
const SORT_ORDER_LABELS: Record<MapPlotRequestDetailFileSortOrder, string> = {
  desc: "降順",
  asc: "昇順",
};

const isPreviewableFile = (file: MapPlotRequestDetailFile): boolean => {
  return isPreviewableMapPlotRequestDetailFileContentType(file.contentType);
};

const FILE_DOWNLOAD_PRIMARY_BUTTON_WIDTH = 128;
const FILE_DOWNLOAD_MENU_BUTTON_WIDTH = 36;
const FILE_DOWNLOAD_ACTION_BUTTON_WIDTH =
  FILE_DOWNLOAD_PRIMARY_BUTTON_WIDTH + FILE_DOWNLOAD_MENU_BUTTON_WIDTH;
const UPLOADED_FILES_ACTION_COLUMN_WIDTH = 260;
const UPLOADED_FILES_DISPLAY_NAME_COLUMN_MIN_WIDTH = 320;
const UPLOADED_FILES_TABLE_MIN_WIDTH = 1000;

const FILE_GROUP_LABEL: Record<MapPlotRequestDetailFileGroup, string> = {
  document: "文書",
  image: "画像",
  spreadsheet: "表計算",
  other: "その他",
};

const renderFileGroupIcon = (
  fileGroup: MapPlotRequestDetailFileGroup
): React.ReactElement => {
  switch (fileGroup) {
    case "document":
      return <DescriptionOutlinedIcon fontSize="small" color="action" />;
    case "image":
      return <ImageOutlinedIcon fontSize="small" color="action" />;
    case "spreadsheet":
      return <TableChartOutlinedIcon fontSize="small" color="action" />;
    case "other":
    default:
      return <InsertDriveFileOutlinedIcon fontSize="small" color="action" />;
  }
};

const buildFileSections = (
  files: MapPlotRequestDetailFile[],
  groupByFileType: boolean
): MapPlotRequestDetailFileSection[] => {
  if (!groupByFileType) {
    return [
      {
        key: "all",
        label: "",
        group: null,
        files,
      },
    ];
  }

  return MAP_PLOT_REQUEST_DETAIL_FILE_GROUPS.map((group) => {
    const groupedFiles = files.filter((file) => file.fileGroup === group);
    return {
      key: group,
      label: FILE_GROUP_LABEL[group],
      group,
      files: groupedFiles,
    };
  }).filter((section) => section.files.length > 0);
};

const getDisplayNameSuffix = (file: MapPlotRequestDetailFile): string => {
  const normalizedExtension = file.fileExtension.trim().replace(/^\.+/, "");

  if (!normalizedExtension) {
    return "";
  }

  return `.${normalizedExtension}`;
};

const stripSupportedTrailingExtension = (value: string): string => {
  const lastDotIndex = value.lastIndexOf(".");

  if (lastDotIndex <= 0) {
    return value;
  }

  const trailingExtension = value.slice(lastDotIndex + 1).toLowerCase();
  if (
    trailingExtension.length === 0 ||
    !MAP_PLOT_REQUEST_DETAIL_ALLOWED_EXTENSIONS.has(trailingExtension)
  ) {
    return value;
  }

  return value.slice(0, lastDotIndex);
};

const normalizeDisplayNameBase = (value: string, suffix: string): string => {
  const normalizedValue = value.trim();

  if (!normalizedValue || !suffix) {
    return normalizedValue;
  }

  if (normalizedValue.toLowerCase().endsWith(suffix.toLowerCase())) {
    return normalizedValue.slice(0, -suffix.length);
  }

  return stripSupportedTrailingExtension(normalizedValue);
};

const getEditableDisplayName = (file: MapPlotRequestDetailFile): string => {
  const suffix = getDisplayNameSuffix(file);

  return normalizeDisplayNameBase(file.displayName, suffix);
};

const buildDisplayNameForSave = (
  file: MapPlotRequestDetailFile,
  displayNameBase: string
): string => {
  const suffix = getDisplayNameSuffix(file);
  const normalizedDisplayNameBase = normalizeDisplayNameBase(
    displayNameBase,
    suffix
  );

  if (!suffix) {
    return normalizedDisplayNameBase;
  }

  return `${normalizedDisplayNameBase}${suffix}`;
};

const getEditableDisplayNameMaxLength = (
  file: MapPlotRequestDetailFile
): number => {
  return Math.max(
    1,
    MAX_DISPLAY_NAME_LENGTH - getDisplayNameSuffix(file).length
  );
};

export const MapPlotRequestDetailFilesView: React.FC<
  MapPlotRequestDetailFilesViewProps
> = ({ detail }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [searchInput, setSearchInput] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [sortBy, setSortBy] = useState<MapPlotRequestDetailFileSortBy>(
    DEFAULT_MAP_PLOT_REQUEST_DETAIL_FILE_SORT_BY
  );
  const [sortOrder, setSortOrder] = useState<MapPlotRequestDetailFileSortOrder>(
    DEFAULT_MAP_PLOT_REQUEST_DETAIL_FILE_SORT_ORDER
  );
  const [groupByFileType, setGroupByFileType] = useState(false);
  const [editingFileId, setEditingFileId] = useState<number | null>(null);
  const [isUpdatingDisplayName, setIsUpdatingDisplayName] = useState(false);
  const {
    register: registerDisplayName,
    handleSubmit: handleDisplayNameFormSubmit,
    reset: resetDisplayNameForm,
    formState: { errors: displayNameErrors },
  } = useForm<DisplayNameFormData>({
    defaultValues: {
      displayName: "",
    },
  });

  const listQuery = useMemo(() => {
    return {
      search: searchKeyword,
      sortBy,
      sortOrder,
    };
  }, [searchKeyword, sortBy, sortOrder]);

  const {
    files,
    totalCount,
    page,
    pageSize,
    changePage,
    changePageSize,
    isLoading,
    isError,
    refetch,
    uploadFiles,
    retryFailedUploads,
    deleteFile,
    updateDisplayName,
    getDownloadInfo,
    isUploading,
    uploadProgress,
    failedUploads,
    lastUploadSummary,
    resetUploadState,
  } = useMapPlotRequestDetailFiles(detail.id, true, listQuery);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedFilesMessage, setSelectedFilesMessage] = useState<
    string | null
  >(null);
  const [downloadingFileId, setDownloadingFileId] = useState<number | null>(
    null
  );
  const [previewMenuAnchorEl, setPreviewMenuAnchorEl] =
    useState<HTMLElement | null>(null);
  const [previewMenuFile, setPreviewMenuFile] =
    useState<MapPlotRequestDetailFile | null>(null);
  const [deleteTargetFile, setDeleteTargetFile] =
    useState<MapPlotRequestDetailFile | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    setSearchInput("");
    setSearchKeyword("");
    setSortBy(DEFAULT_MAP_PLOT_REQUEST_DETAIL_FILE_SORT_BY);
    setSortOrder(DEFAULT_MAP_PLOT_REQUEST_DETAIL_FILE_SORT_ORDER);
    setGroupByFileType(false);
    setEditingFileId(null);
    resetDisplayNameForm();

    setSelectedFiles([]);
    setSelectedFilesMessage(null);
    setDownloadingFileId(null);
    setPreviewMenuAnchorEl(null);
    setPreviewMenuFile(null);
    setDeleteTargetFile(null);
    resetUploadState();
  }, [detail.id, resetDisplayNameForm, resetUploadState]);

  const selectedFilesTotalSize = useMemo(() => {
    return selectedFiles.reduce((sum, file) => {
      return sum + file.size;
    }, 0);
  }, [selectedFiles]);

  const fileSections = useMemo(() => {
    return buildFileSections(files, groupByFileType);
  }, [files, groupByFileType]);

  const handleSearch = (): void => {
    setSearchKeyword(searchInput.trim());
  };

  const handleClearSearch = (): void => {
    setSearchInput("");
    setSearchKeyword("");
  };

  const handleSelectButtonClick = (): void => {
    fileInputRef.current?.click();
  };

  const handleStartEditDisplayName = (file: MapPlotRequestDetailFile): void => {
    setEditingFileId(file.id);
    resetDisplayNameForm({
      displayName: getEditableDisplayName(file),
    });
  };

  const handleCancelEditDisplayName = (): void => {
    setEditingFileId(null);
    resetDisplayNameForm();
  };

  const handleSaveDisplayName = async (
    file: MapPlotRequestDetailFile,
    data: DisplayNameFormData
  ): Promise<void> => {
    const normalizedDisplayName = buildDisplayNameForSave(
      file,
      data.displayName
    );

    if (normalizedDisplayName === file.displayName) {
      handleCancelEditDisplayName();
      return;
    }

    setIsUpdatingDisplayName(true);
    try {
      await updateDisplayName(file.id, normalizedDisplayName);
      toast.success("表示名を更新しました");
      handleCancelEditDisplayName();
    } catch (error) {
      toast.error(
        extractMapPlotRequestDetailFileErrorMessage(
          error,
          "表示名の更新に失敗しました"
        )
      );
    } finally {
      setIsUpdatingDisplayName(false);
    }
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const pickedFiles = Array.from(event.target.files ?? []);

    if (pickedFiles.length === 0) {
      return;
    }

    const acceptedFiles = pickedFiles.filter((file) => {
      return isSupportedMapPlotRequestDetailFile(file.name);
    });
    const rejectedFiles = pickedFiles.filter((file) => {
      return !isSupportedMapPlotRequestDetailFile(file.name);
    });

    if (acceptedFiles.length > 0) {
      setSelectedFiles((prev) => {
        return [...prev, ...acceptedFiles];
      });
    }

    if (rejectedFiles.length > 0) {
      setSelectedFilesMessage(
        `${formatNumberWithComma(
          rejectedFiles.length
        )}件は対応していない拡張子のため除外しました。`
      );
    } else {
      setSelectedFilesMessage(null);
    }

    event.target.value = "";
  };

  const handleUpload = async (): Promise<void> => {
    if (selectedFiles.length === 0) {
      setSelectedFilesMessage("アップロードするファイルを選択してください。");
      return;
    }

    const summary = await uploadFiles(selectedFiles);

    if (summary.successCount > 0) {
      toast.success(
        `${formatNumberWithComma(
          summary.successCount
        )}件の資料をアップロードしました`
      );
    }

    if (summary.failedCount > 0) {
      toast.error(
        `${formatNumberWithComma(
          summary.failedCount
        )}件の資料のアップロードに失敗しました`
      );
    }

    setSelectedFiles([]);
  };

  const handleRetryFailedUploads = async (): Promise<void> => {
    const summary = await retryFailedUploads();

    if (summary.successCount > 0) {
      toast.success(
        `${formatNumberWithComma(
          summary.successCount
        )}件の資料の再アップロードに成功しました`
      );
    }

    if (summary.failedCount > 0) {
      toast.error(
        `${formatNumberWithComma(
          summary.failedCount
        )}件の資料の再アップロードに失敗しました`
      );
    }
  };

  const handlePreviewMenuOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    file: MapPlotRequestDetailFile
  ): void => {
    setPreviewMenuAnchorEl(event.currentTarget);
    setPreviewMenuFile(file);
  };

  const handlePreviewMenuClose = (): void => {
    setPreviewMenuAnchorEl(null);
    setPreviewMenuFile(null);
  };

  const openDownloadUrl = (url: string): void => {
    const openedWindow = window.open(url, "_blank", "noopener,noreferrer");

    if (openedWindow === null) {
      window.location.assign(url);
    }
  };

  const handleDownload = async (
    file: MapPlotRequestDetailFile
  ): Promise<void> => {
    setDownloadingFileId(file.id);

    try {
      const downloadInfo = await getDownloadInfo(file.id, false);
      openDownloadUrl(downloadInfo.downloadUrl);
    } catch (error) {
      toast.error(
        extractMapPlotRequestDetailFileErrorMessage(
          error,
          "資料のダウンロードに失敗しました"
        )
      );
    } finally {
      setDownloadingFileId(null);
    }
  };

  const handlePreview = async (
    file: MapPlotRequestDetailFile
  ): Promise<void> => {
    const previewWindow: Window | null = window.open("", "_blank");

    if (previewWindow === null) {
      toast.error(
        "ポップアップがブロックされました。ブラウザの設定を確認してください"
      );
      return;
    }

    previewWindow.opener = null;

    try {
      const downloadInfo = await getDownloadInfo(file.id, true);

      previewWindow.location.assign(downloadInfo.downloadUrl);
    } catch (error) {
      if (previewWindow !== null && !previewWindow.closed) {
        previewWindow.close();
      }
      toast.error(
        extractMapPlotRequestDetailFileErrorMessage(
          error,
          "資料のプレビューに失敗しました"
        )
      );
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!deleteTargetFile) {
      return;
    }

    setIsDeleting(true);
    try {
      await deleteFile(deleteTargetFile.id);
      toast.success("資料を削除しました");
      setDeleteTargetFile(null);
    } catch (error) {
      toast.error(
        extractMapPlotRequestDetailFileErrorMessage(
          error,
          "資料の削除に失敗しました"
        )
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const renderDisplayName = (
    file: MapPlotRequestDetailFile
  ): React.ReactNode => {
    const isEditingTarget = editingFileId === file.id;

    if (isEditingTarget) {
      const displayNameSuffix = getDisplayNameSuffix(file);

      return (
        <Box
          component="form"
          onSubmit={(event: React.FormEvent<HTMLFormElement>) => {
            void handleDisplayNameFormSubmit(async (data) => {
              await handleSaveDisplayName(file, data);
            })(event);
          }}
          sx={{ width: "100%" }}
        >
          <Stack spacing={0.75} sx={{ width: "100%" }}>
            <TextField
              size="small"
              fullWidth
              {...registerDisplayName("displayName", {
                validate: (value: string) => {
                  return (
                    normalizeDisplayNameBase(value, displayNameSuffix).length >
                      0 || "表示名を入力してください"
                  );
                },
              })}
              inputProps={{
                maxLength: getEditableDisplayNameMaxLength(file),
              }}
              InputProps={{
                endAdornment: displayNameSuffix ? (
                  <InputAdornment position="end">
                    {displayNameSuffix}
                  </InputAdornment>
                ) : undefined,
              }}
              disabled={isUpdatingDisplayName}
              error={displayNameErrors.displayName !== undefined}
              helperText={
                displayNameErrors.displayName?.message ??
                "拡張子は変更できません"
              }
            />
            <Stack
              direction="row"
              spacing={1}
              justifyContent="flex-end"
              flexWrap="wrap"
            >
              <Button
                size="small"
                variant="outlined"
                type="button"
                onClick={handleCancelEditDisplayName}
                disabled={isUpdatingDisplayName}
              >
                キャンセル
              </Button>
              <Button
                size="small"
                variant="contained"
                type="submit"
                disabled={isUpdatingDisplayName}
              >
                保存
              </Button>
            </Stack>
          </Stack>
        </Box>
      );
    }

    return (
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, overflowWrap: "anywhere" }}
        >
          {file.displayName}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            handleStartEditDisplayName(file);
          }}
          disabled={
            isUploading ||
            isDeleting ||
            isUpdatingDisplayName ||
            downloadingFileId === file.id
          }
        >
          表示名変更
        </Button>
      </Stack>
    );
  };

  const renderFileActions = (
    file: MapPlotRequestDetailFile,
    justifyContent: "center" | "flex-start"
  ): React.ReactNode => {
    const isActionDisabled =
      isUploading ||
      isDeleting ||
      isUpdatingDisplayName ||
      editingFileId === file.id;
    const isDownloading = downloadingFileId === file.id;

    return (
      <Stack direction="row" spacing={1} justifyContent={justifyContent}>
        {isPreviewableFile(file) ? (
          <ButtonGroup
            variant="outlined"
            size="small"
            sx={{
              width: FILE_DOWNLOAD_ACTION_BUTTON_WIDTH,
              "& .MuiButtonGroup-grouped": {
                minWidth: 0,
              },
            }}
          >
            <Button
              sx={{
                width: FILE_DOWNLOAD_PRIMARY_BUTTON_WIDTH,
                whiteSpace: "nowrap",
              }}
              startIcon={<DownloadIcon />}
              onClick={() => {
                void handleDownload(file);
              }}
              disabled={isActionDisabled || isDownloading}
            >
              ダウンロード
            </Button>
            <Button
              aria-label={`${file.displayName} のプレビュー操作`}
              onClick={(event) => {
                handlePreviewMenuOpen(event, file);
              }}
              disabled={isActionDisabled || isDownloading}
              sx={{ px: 1, minWidth: FILE_DOWNLOAD_MENU_BUTTON_WIDTH }}
            >
              <ArrowDropDownIcon fontSize="small" />
            </Button>
          </ButtonGroup>
        ) : (
          <Button
            variant="outlined"
            size="small"
            sx={{
              width: FILE_DOWNLOAD_ACTION_BUTTON_WIDTH,
              whiteSpace: "nowrap",
            }}
            startIcon={<DownloadIcon />}
            onClick={() => {
              void handleDownload(file);
            }}
            disabled={isActionDisabled || isDownloading}
          >
            ダウンロード
          </Button>
        )}
        <Button
          variant="outlined"
          size="small"
          color="error"
          onClick={() => {
            setDeleteTargetFile(file);
          }}
          disabled={isActionDisabled || isDownloading}
        >
          削除
        </Button>
      </Stack>
    );
  };

  const getEmptyMessage = (): string => {
    if (searchKeyword) {
      return `「${searchKeyword}」に一致する資料はありません。`;
    }

    return "このピンにはまだ資料がありません。";
  };

  return (
    <>
      <Stack spacing={2}>
        <SectionPaper>
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              資料アップロード
            </Typography>

            <Stack spacing={1.5}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  hidden
                  onChange={handleFileChange}
                />
                <Button
                  variant="outlined"
                  onClick={handleSelectButtonClick}
                  disabled={isUploading || isUpdatingDisplayName}
                >
                  ファイル選択
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    void handleUpload();
                  }}
                  disabled={
                    isUploading ||
                    isUpdatingDisplayName ||
                    selectedFiles.length === 0
                  }
                >
                  アップロード
                </Button>
                <Typography variant="caption" color="text.secondary">
                  対応形式: PDF / Word（.doc, .docx）/ 画像（.jpg, .jpeg, .png,
                  .gif）/ Excel（.xls, .xlsx）/ CSV（.csv）
                </Typography>
              </Stack>

              {selectedFiles.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  ファイル未選択
                </Typography>
              ) : (
                <Stack spacing={1}>
                  <Typography variant="body2" color="text.secondary">
                    {`選択中: ${formatNumberWithComma(
                      selectedFiles.length
                    )}件（合計 ${formatMapPlotRequestDetailFileSize(
                      selectedFilesTotalSize
                    )}）`}
                  </Typography>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      maxHeight: 180,
                      overflowY: "auto",
                    }}
                  >
                    <Stack spacing={0.75}>
                      {selectedFiles.map((file, index) => {
                        return (
                          <Box
                            key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              gap: 1,
                              alignItems: "flex-start",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ wordBreak: "break-all" }}
                            >
                              {`${index + 1}. ${file.name}`}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ flexShrink: 0, mt: 0.25 }}
                            >
                              {formatMapPlotRequestDetailFileSize(file.size)}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Stack>
                  </Box>
                </Stack>
              )}
              {selectedFilesMessage && (
                <Typography variant="body2" color="error">
                  {selectedFilesMessage}
                </Typography>
              )}

              {isUploading && (
                <Stack spacing={0.8}>
                  <LinearProgress
                    variant="determinate"
                    value={
                      uploadProgress.total === 0
                        ? 0
                        : (uploadProgress.processed / uploadProgress.total) *
                          100
                    }
                  />
                  <Typography variant="body2" color="text.secondary">
                    {`${formatNumberWithComma(
                      uploadProgress.processed
                    )}/${formatNumberWithComma(
                      uploadProgress.total
                    )} 件処理中（成功 ${formatNumberWithComma(
                      uploadProgress.successCount
                    )} / 失敗 ${formatNumberWithComma(
                      uploadProgress.failedCount
                    )}）`}
                  </Typography>
                  {uploadProgress.currentFileName && (
                    <Typography variant="caption" color="text.secondary">
                      処理中: {uploadProgress.currentFileName}
                    </Typography>
                  )}
                </Stack>
              )}

              {lastUploadSummary && !isUploading && (
                <Typography variant="body2" color="text.secondary">
                  {`直近結果: 成功 ${formatNumberWithComma(
                    lastUploadSummary.successCount
                  )}件 / 失敗 ${formatNumberWithComma(
                    lastUploadSummary.failedCount
                  )}件`}
                </Typography>
              )}

              {failedUploads.length > 0 && (
                <Box
                  sx={{
                    p: 1.5,
                    border: "1px solid",
                    borderColor: "error.main",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                    アップロードに失敗したファイルがあります。
                  </Typography>
                  <Stack spacing={0.5} sx={{ mb: 1 }}>
                    {failedUploads.map((failedItem, index) => {
                      return (
                        <Typography
                          key={`${failedItem.file.name}-${failedItem.file.size}-${failedItem.file.lastModified}-${index}`}
                          variant="caption"
                          color="text.secondary"
                        >
                          {`${failedItem.file.name}: ${failedItem.message}`}
                        </Typography>
                      );
                    })}
                  </Stack>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      void handleRetryFailedUploads();
                    }}
                    disabled={isUploading || isUpdatingDisplayName}
                  >
                    失敗分を再試行
                  </Button>
                </Box>
              )}
            </Stack>
          </Box>
        </SectionPaper>

        <SectionPaper>
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              アップロード済み資料
            </Typography>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1}
              alignItems={{ xs: "stretch", md: "center" }}
              sx={{ mb: 2 }}
            >
              <TextField
                label="資料検索"
                placeholder="表示名・元ファイル名"
                size="small"
                value={searchInput}
                onChange={(event) => {
                  setSearchInput(event.target.value);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    handleSearch();
                  }
                }}
                sx={{ width: { xs: "100%", md: 320 } }}
              />
              <Button
                variant="outlined"
                onClick={handleSearch}
                disabled={isUpdatingDisplayName}
              >
                検索
              </Button>
              <Button
                variant="text"
                onClick={handleClearSearch}
                disabled={isUpdatingDisplayName}
              >
                クリア
              </Button>
              <Stack spacing={0.5} sx={{ width: { xs: "100%", md: 200 } }}>
                <Typography variant="caption" color="text.secondary">
                  ソート項目
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={sortBy}
                    onChange={(event) => {
                      setSortBy(
                        event.target.value as MapPlotRequestDetailFileSortBy
                      );
                    }}
                    disabled={isUpdatingDisplayName}
                  >
                    {MAP_PLOT_REQUEST_DETAIL_FILE_SORT_BY_VALUES.map(
                      (value) => {
                        return (
                          <MenuItem key={value} value={value}>
                            {SORT_BY_LABELS[value]}
                          </MenuItem>
                        );
                      }
                    )}
                  </Select>
                </FormControl>
              </Stack>
              <Stack spacing={0.5} sx={{ width: { xs: "100%", md: 140 } }}>
                <Typography variant="caption" color="text.secondary">
                  並び順
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={sortOrder}
                    onChange={(event) => {
                      setSortOrder(
                        event.target.value as MapPlotRequestDetailFileSortOrder
                      );
                    }}
                    disabled={isUpdatingDisplayName}
                  >
                    {MAP_PLOT_REQUEST_DETAIL_FILE_SORT_ORDER_VALUES.map(
                      (value) => {
                        return (
                          <MenuItem key={value} value={value}>
                            {SORT_ORDER_LABELS[value]}
                          </MenuItem>
                        );
                      }
                    )}
                  </Select>
                </FormControl>
              </Stack>
              <FormControlLabel
                sx={{ ml: { xs: 0, md: 0.5 } }}
                control={
                  <Switch
                    checked={groupByFileType}
                    onChange={(event) => {
                      setGroupByFileType(event.target.checked);
                    }}
                  />
                }
                label="形式でグループ表示"
              />
            </Stack>
            {isLoading ? (
              <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
                <CircularProgress />
              </Box>
            ) : isError ? (
              <Stack spacing={1.5} alignItems="center" sx={{ py: 4 }}>
                <Typography color="error">
                  資料一覧の取得に失敗しました。
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => {
                    void refetch();
                  }}
                >
                  再読み込み
                </Button>
              </Stack>
            ) : files.length === 0 ? (
              <Box sx={{ py: 4 }}>
                <Typography color="text.secondary">
                  {getEmptyMessage()}
                </Typography>
              </Box>
            ) : isMobile ? (
              <Stack spacing={1.5}>
                {fileSections.map((section) => {
                  return (
                    <Stack key={section.key} spacing={1}>
                      {groupByFileType && section.group !== null && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          {renderFileGroupIcon(section.group)}
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {`${section.label}（${formatNumberWithComma(
                              section.files.length
                            )}件）`}
                          </Typography>
                        </Stack>
                      )}

                      <Stack spacing={1.25}>
                        {section.files.map((file) => {
                          return (
                            <Box
                              key={file.id}
                              sx={{
                                p: 1.5,
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                              }}
                            >
                              <Stack spacing={0.8}>
                                {renderDisplayName(file)}
                                <Stack
                                  direction="row"
                                  spacing={0.75}
                                  alignItems="center"
                                >
                                  {renderFileGroupIcon(file.fileGroup)}
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {`形式: ${file.fileExtension.toUpperCase()} / サイズ: ${formatMapPlotRequestDetailFileSize(
                                      file.fileSize
                                    )}`}
                                  </Typography>
                                </Stack>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  アップロード日時:{" "}
                                  {formatMapPlotRequestDateTime(file.createdAt)}
                                </Typography>
                              </Stack>

                              <Box sx={{ mt: 1.25 }}>
                                {renderFileActions(file, "flex-start")}
                              </Box>
                            </Box>
                          );
                        })}
                      </Stack>
                    </Stack>
                  );
                })}
              </Stack>
            ) : (
              <Box sx={{ width: "100%", overflowX: "auto" }}>
                <Table
                  size="small"
                  sx={{ minWidth: UPLOADED_FILES_TABLE_MIN_WIDTH }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell width={120}>形式</TableCell>
                      <TableCell
                        sx={{
                          minWidth:
                            UPLOADED_FILES_DISPLAY_NAME_COLUMN_MIN_WIDTH,
                        }}
                      >
                        表示名
                      </TableCell>
                      <TableCell width={120}>サイズ</TableCell>
                      <TableCell width={180}>アップロード日時</TableCell>
                      <TableCell
                        width={UPLOADED_FILES_ACTION_COLUMN_WIDTH}
                        align="center"
                      >
                        操作
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fileSections.map((section) => {
                      return (
                        <Fragment key={section.key}>
                          {groupByFileType && section.group !== null && (
                            <TableRow sx={{ backgroundColor: "action.hover" }}>
                              <TableCell colSpan={5}>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  {renderFileGroupIcon(section.group)}
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 700 }}
                                  >
                                    {`${section.label}（${formatNumberWithComma(
                                      section.files.length
                                    )}件）`}
                                  </Typography>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          )}
                          {section.files.map((file) => {
                            return (
                              <TableRow key={file.id}>
                                <TableCell>
                                  <Stack
                                    direction="row"
                                    spacing={0.75}
                                    alignItems="center"
                                  >
                                    {renderFileGroupIcon(file.fileGroup)}
                                    <Typography variant="body2">
                                      {file.fileExtension.toUpperCase()}
                                    </Typography>
                                  </Stack>
                                </TableCell>
                                <TableCell
                                  sx={{
                                    minWidth:
                                      UPLOADED_FILES_DISPLAY_NAME_COLUMN_MIN_WIDTH,
                                  }}
                                >
                                  {renderDisplayName(file)}
                                </TableCell>
                                <TableCell>
                                  {formatMapPlotRequestDetailFileSize(
                                    file.fileSize
                                  )}
                                </TableCell>
                                <TableCell>
                                  {formatMapPlotRequestDateTime(file.createdAt)}
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{
                                    minWidth:
                                      UPLOADED_FILES_ACTION_COLUMN_WIDTH,
                                  }}
                                >
                                  {renderFileActions(file, "center")}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )}

            {!isLoading && !isError && totalCount > 0 && (
              <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end" }}>
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  rowsPerPage={pageSize}
                  rowsPerPageOptions={[20, 50, 100]}
                  onPageChange={(_, newPage) => {
                    changePage(newPage);
                  }}
                  onRowsPerPageChange={(event) => {
                    changePageSize(Number(event.target.value));
                  }}
                  labelRowsPerPage="ページあたりの行数:"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from.toLocaleString()}〜${to.toLocaleString()} / ${count.toLocaleString()}`
                  }
                />
              </Box>
            )}
          </Box>
        </SectionPaper>
      </Stack>

      <DeletePlotDetailFileConfirmModal
        open={deleteTargetFile !== null}
        fileName={deleteTargetFile?.displayName ?? ""}
        isDeleting={isDeleting}
        onClose={() => {
          if (!isDeleting) {
            setDeleteTargetFile(null);
          }
        }}
        onDelete={() => {
          void handleDelete();
        }}
      />
      <Menu
        anchorEl={previewMenuAnchorEl}
        open={previewMenuAnchorEl !== null}
        onClose={handlePreviewMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <MenuItem
          onClick={() => {
            if (!previewMenuFile) {
              return;
            }

            void handlePreview(previewMenuFile);
            handlePreviewMenuClose();
          }}
        >
          プレビュー
        </MenuItem>
      </Menu>
    </>
  );
};
